import type { ReactNode } from 'react'

import { CaseScreen } from '@/screens/Case/CaseScreen'
import { CoursesScreen } from '@/screens/Courses/CoursesScreen'
import { GuideScreen } from '@/screens/Guide/GuideScreen'
import { IntelligenceScreen } from '@/screens/Intelligence/IntelligenceScreen'
import { NewCaseScreen } from '@/screens/NewCase/NewCaseScreen'
import { OppositionScreen } from '@/screens/Opposition/OppositionScreen'
import { QueueScreen } from '@/screens/Queue/QueueScreen'
import { WorkbenchScreen } from '@/screens/Workbench/WorkbenchScreen'
import type { TabDef } from '@/routes'

/**
 * Which tabs render a real screen, and which are still a placeholder.
 *
 * Its own module, and not for tidiness. `App.tsx` is a component module, so
 * exporting a data map from it costs Vite's fast refresh: a file that exports
 * anything other than components falls back to a full page reload on every
 * edit, and `eslint --max-warnings 0` fails the build over it
 * (`react-refresh/only-export-components`). Two files in this tree suppress
 * that rule — `Toast.tsx` and `AuthProvider.tsx` — because a context and its
 * provider genuinely cannot be separated. This map can be, so it is.
 *
 * Exported so a test can assert it. The whole reason `test_app_routes.py`
 * exists on the server side is that P0 shipped with every router written,
 * every router tested, and the block that mounts them empty — so the entire
 * product API was 404 and nothing noticed. The frontend has exactly the same
 * failure available to it: `QueueScreen` and `CaseScreen` sat in the tree as
 * dead code for a whole build, type-checking cleanly and rendering nowhere,
 * because nothing imported them. `App.mounting.test.tsx` compares this map
 * against the phases in `routes.ts` precisely so that cannot recur, and it
 * reads the map from here rather than through `App` — a test about which tabs
 * are mounted has no business pulling in the auth shell and the Admin glob to
 * find out.
 *
 * `workbench` joined the mounted set in chunk 7 — a narrower slice of the
 * full P5 vision (see `WorkbenchScreen`'s own module docstring for exactly
 * what that slice is); `intelligence` joined in chunk 9, likewise a
 * narrower slice of the full P7 vision (see `IntelligenceScreen`'s own
 * module docstring). `routes.ts` marks both `phase` `'P2'` for the same
 * reason the other four built screens do, even though the docket's own
 * chunk numbering calls them P5 and P7 respectively — this map, and the
 * test that checks it, use `'P2'` as "mounted", not as a historical phase
 * label.
 *
 * The screens are imported directly rather than discovered the way
 * `screens/Admin/**` is: they are this application's own to mount, and the
 * glob indirection in `App.tsx` exists only because Admin belonged to a
 * different agent.
 */
export const SCREENS: Partial<Record<TabDef['key'], () => ReactNode>> = {
  new: () => <NewCaseScreen />,
  opposition: () => <OppositionScreen />,
  queue: () => <QueueScreen />,
  case: () => <CaseScreen />,
  intelligence: () => <IntelligenceScreen />,
  workbench: () => <WorkbenchScreen />,
  guide: () => <GuideScreen />,
  courses: () => <CoursesScreen />,
}
